import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
    Autocomplete,
    Button,
    Checkbox,
    Dialog,
    FormControlLabel,
    FormHelperText,
    ListItem,
    Stack,
    TextField
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers-pro';
import { useMuiController, useMuiForm } from '@zenput/util-common';
import { endOfDay, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { FieldError } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { useDebounce } from 'usehooks-ts';
import { ZodIssueCode } from 'zod';
import { Locations } from '../../../backbone-unorganized/models';
import AppBar from '../../shared/AppBar';
import BottomBar from '../../shared/BottomBar';
import PageLayout from '../../shared/PageLayout';
import { DeleteFollowUpActionModal } from './DeleteFollowUpActionModal';

import {
    InitialFollowUpActionType,
    InitialFollowUpActionSchema,
    TemporalFollowUpActionType,
    ZenputFileType
} from './FollowUpActions.types';
import { FollowUpActionsGallery } from './FollowUpActionsGallery';
import { LocationIdLabelPair, LocationsQueryParams } from './types';

const useStyles = makeStyles()((theme) => {
    const { spacing, typography } = theme;

    return {
        bottomBar: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: spacing(2),
            textTransform: 'none',
            backgroundColor: 'white'
        },
        bottomButton: {
            textTransform: 'none',
            padding: `${spacing(1.5)} 0`
        },
        formContainer: {
            backgroundColor: 'white',
            marginTop: spacing(2),
            marginBottom: spacing(16),
            padding: spacing(2)
        },
        formInput: {
            marginTop: spacing(2),
            '& .MuiInputBase-input': {
                border: 'none',
                padding: `${spacing(2)} 0`,
                borderBottom: 'groove'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                paddingLeft: spacing(1.5),
                paddingTop: spacing(4.5),
                color: 'black !important'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        },
        textFieldInput: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                border: 'none',
                background: 'none'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                fontSize: typography.body2.fontSize,
                color: 'black !important'
            }
        },
        dateFieldInput: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                padding: `${spacing(2.5)} 0`,
                border: 'none',
                color: 'black !important'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
                borderBottom: 'groove'
            },
            '& .MuiOutlinedInput-notchedOutline:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                paddingLeft: spacing(1.5),
                paddingTop: spacing(4.5),
                color: 'black !important'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        },
        autoSelect: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                minHeight: '36px',
                border: 'none',
                background: 'none',
                padding: `${spacing(2)} 0`,
                color: 'black !important'
            },
            '& .MuiFormControl-root': {
                height: 'auto'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        }
    };
});

export const TITLE_MAX_CHARACTERS = 255;
export const DESCRIPTION_MAX_CHARACTERS = 5000;
export type FollowUpActionMode = 'add' | 'edit';

export const toDate = (value: Date | string) => {
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
        const parsed = parseISO(value);
        return parsed;
    }
    return null;
};

const getToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

const emptyFollowUpAction = {
    title: '',
    description: '',
    require_photo: false,
    due_date: null,
    location: null,
    images: []
};

interface FollowUpActionsFormProps {
    allowAccessToReportingLocations: boolean;
    handleFollowUpAction: (fua: TemporalFollowUpActionType) => void;
    initialFollowUpAction: TemporalFollowUpActionType;
    mode: FollowUpActionMode;
    onGoBack: () => void;
    open: boolean;
    selectedLocation: LocationIdLabelPair | null;
    uploadFollowUpFiles: (files: ZenputFileType[]) => string[];
}

export const FollowUpActionForm = (props: FollowUpActionsFormProps) => {
    const {
        allowAccessToReportingLocations,
        handleFollowUpAction,
        initialFollowUpAction,
        mode,
        open,
        onGoBack,
        selectedLocation,
        uploadFollowUpFiles
    } = props;
    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [locations, setLocations] = useState<{ id: number; label: string }[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const isEditMode = mode === 'edit';

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors }
    } = useMuiForm<InitialFollowUpActionType>({
        resolver: zodResolver(InitialFollowUpActionSchema),
        defaultValues: emptyFollowUpAction
    });

    const [locationSearch, setLocationSearch] = useState('');
    const debouncedLocationSearch = useDebounce(locationSearch, 200);
    const [s3keys, setS3Keys] = useState([]);

    const handleAddS3Keys = (keys) => {
        setS3Keys((prev) => prev.concat(keys));
    };

    const handleDeleteS3keys = (indexToRemove) => {
        setS3Keys((prevKeys) => prevKeys.filter((_, index) => index !== indexToRemove));
    };

    const getError = (error?: FieldError) => {
        const zodErrorCodeMap: Record<string, string | undefined> = {
            invalid_type: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            too_small: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            too_big: _t('mobile_mobileform_add_follow_up_actions_forms_too_many_characters')
        } satisfies Partial<Record<ZodIssueCode, string>>;

        return error
            ? {
                  error: true,
                  helperText: zodErrorCodeMap[error.type] ?? _t('mobile_mobileform_add_follow_up_actions_forms_generic')
              }
            : {};
    };

    const { field: titleField } = useMuiController({ control, name: 'title' });
    const { field: descriptionField } = useMuiController({ control, name: 'description' });
    const { field: requirePhotoField } = useMuiController({ control, name: 'require_photo' });
    const { field: dueDateField } = useMuiController({ control, name: 'due_date' });
    const { field: locationField } = useMuiController({ control, name: 'location', defaultValue: selectedLocation });
    const { field: imagesField } = useMuiController({ control, name: 'images' });

    if (selectedLocation) {
        emptyFollowUpAction.location = selectedLocation;
    }

    const requestParams: LocationsQueryParams = allowAccessToReportingLocations
        ? {
              // For admins and auditors
              allow_cross_company_access: allowAccessToReportingLocations,
              limit: 500,
              order_by: 'name',
              search: debouncedLocationSearch
          }
        : {
              // For everyone else, including FBCs
              include_fbc_locations: true,
              limit: 500,
              order_by: 'name',
              search: debouncedLocationSearch
          };

    useEffect(() => {
        const mobileLocations = new Locations([], requestParams);
        mobileLocations.fetch({
            success: function () {
                const locationData = mobileLocations.models.map((model) => ({
                    id: model.get('id'),
                    label: model.get('name') || ''
                }));
                setLocations(locationData);
                setLoading(false);
            },
            error: function () {
                enqueueSnackbar(JSON.stringify(_t('mobile_mobileform_add_follow_up_actions_error_locations')), {
                    variant: 'error'
                });
                setLoading(false);
            }
        });
    }, []);

    useEffect(() => {
        if (mode == 'edit') {
            const correctDate = toDate(initialFollowUpAction.due_date);
            reset({
                title: initialFollowUpAction.title,
                description: initialFollowUpAction.description,
                require_photo: initialFollowUpAction.require_photo || false,
                due_date: correctDate,
                location: initialFollowUpAction.location,
                images: initialFollowUpAction.images || []
            });
            if (initialFollowUpAction.s3keys) {
                setS3Keys(initialFollowUpAction.s3keys);
            }
        } else {
            reset(emptyFollowUpAction);
            setS3Keys([]);
        }
    }, [initialFollowUpAction, open, mode]);

    const handleDiscardOrDelete = () => {
        if (isEditMode) {
            setShowDeleteModal(true);
        } else {
            onGoBack();
        }
    };

    const handleDeleteFollowUpAction = () => {
        const tempData = { ...initialFollowUpAction, delete: true };
        handleFollowUpAction(tempData);
        setShowDeleteModal(false);
    };

    const onSubmit = handleSubmit((data) => {
        let tempData: TemporalFollowUpActionType = data;
        if (isEditMode) {
            tempData = { ...data, temp_id: initialFollowUpAction.temp_id, s3keys: s3keys };
        } else {
            tempData = { ...data, s3keys: s3keys };
        }
        handleFollowUpAction(tempData);
        setS3Keys([]);
    });

    return (
        <>
            <Dialog
                fullScreen
                onClose={onGoBack}
                open={open}
                slotProps={{
                    paper: {
                        sx: {
                            overflow: 'hidden'
                        }
                    }
                }}
            >
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <PageLayout>
                        {{
                            appBar: (
                                <AppBar
                                    onGoBack={onGoBack}
                                    title={_t('mobile_mobileform_add_follow_up_actions_add_follow_up')}
                                />
                            ),
                            content: (
                                <div className={classes.formContainer}>
                                    <Stack component="form" onSubmit={onSubmit}>
                                        <FollowUpActionsGallery
                                            isReply={false}
                                            handleAddS3Keys={handleAddS3Keys}
                                            handleDeleteS3keys={handleDeleteS3keys}
                                            uploadFollowUpFiles={uploadFollowUpFiles}
                                            {...imagesField}
                                        />

                                        <TextField
                                            className={classes.formInput}
                                            label={_t('mobile_mobileform_add_follow_up_actions_forms_title')}
                                            required
                                            variant="filled"
                                            {...titleField}
                                            {...getError(errors.title)}
                                        />
                                        <TextField
                                            className={classes.textFieldInput}
                                            data-testid="controlled-input2"
                                            error={getError(errors.description).error}
                                            multiline
                                            label={_t('mobile_mobileform_add_follow_up_actions_forms_description')}
                                            required
                                            variant="filled"
                                            {...descriptionField}
                                        />
                                        {errors.description && (
                                            <FormHelperText sx={{ marginLeft: 2 }} error>
                                                {getError(errors.description).helperText}
                                            </FormHelperText>
                                        )}

                                        <FormControlLabel
                                            sx={{ padding: 1 }}
                                            control={
                                                <Checkbox
                                                    checked={requirePhotoField.value}
                                                    sx={{ marginLeft: 1 }}
                                                    {...requirePhotoField}
                                                />
                                            }
                                            label={_t('mobile_mobileform_add_follow_up_actions_forms_photo')}
                                        />
                                        <MobileDatePicker
                                            className={classes.dateFieldInput}
                                            label={_t('mobile_mobileform_add_follow_up_actions_forms_due_date')}
                                            minDate={getToday()}
                                            slotProps={{
                                                textField: {
                                                    variant: 'filled',
                                                    slotProps: {
                                                        input: { endAdornment: <CalendarTodayIcon /> }
                                                    },
                                                    required: true,
                                                    error: getError(errors.due_date).error
                                                }
                                            }}
                                            value={dueDateField.value ?? null}
                                            onChange={(newDate) => {
                                                dueDateField.onChange(newDate ? endOfDay(newDate) : null);
                                            }}
                                        />
                                        {errors.due_date && (
                                            <FormHelperText sx={{ marginLeft: 2 }} error>
                                                {getError(errors.due_date).helperText}
                                            </FormHelperText>
                                        )}

                                        <Autocomplete
                                            className={classes.autoSelect}
                                            disabled={loading}
                                            disablePortal
                                            getOptionKey={(option) => option.id}
                                            getOptionLabel={(option) => option.label}
                                            onChange={(_event, newValue) => {
                                                locationField.onChange(newValue);
                                            }}
                                            options={locations}
                                            value={locationField.value}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={_t('mobile_common_locations')}
                                                    onChange={(e) => setLocationSearch(e.target.value)}
                                                    required
                                                    sx={{ height: 60 }}
                                                    value={locationSearch}
                                                    variant="filled"
                                                    error={getError(errors.location as FieldError).error}
                                                />
                                            )}
                                            renderOption={(renderProps, option) => (
                                                <ListItem {...renderProps}>{option.label}</ListItem>
                                            )}
                                        />
                                        {'location' in errors && (
                                            <FormHelperText sx={{ marginLeft: 2 }} error>
                                                {getError(errors.location as FieldError).helperText}
                                            </FormHelperText>
                                        )}
                                    </Stack>
                                </div>
                            ),
                            bottomBar: (
                                <BottomBar>
                                    <div className={classes.bottomBar}>
                                        <Button
                                            className={classes.bottomButton}
                                            color="primary"
                                            data-testid="addFollowUpButton"
                                            onClick={onSubmit}
                                            variant="contained"
                                        >
                                            {isEditMode
                                                ? _t('mobile_mobileform_add_follow_up_actions_forms_update_button')
                                                : _t('mobile_mobileform_add_follow_up_actions_add_follow_up')}
                                        </Button>
                                        <Button
                                            className={classes.bottomButton}
                                            color={isEditMode ? 'error' : 'info'}
                                            onClick={handleDiscardOrDelete}
                                        >
                                            {isEditMode
                                                ? _t('mobile_drafts_button_delete')
                                                : _t('mobile_common_button_discard')}
                                        </Button>
                                    </div>
                                </BottomBar>
                            )
                        }}
                    </PageLayout>
                </LocalizationProvider>
            </Dialog>
            <DeleteFollowUpActionModal
                description={_t('mobile_mobileform_add_follow_up_actions_forms_delete_modal_subtitle')}
                handleCloseConfirmation={() => setShowDeleteModal(false)}
                no={_t('mobile_action_sheet_cancel')}
                onSubmit={handleDeleteFollowUpAction}
                showConfirmation={showDeleteModal}
                title={_t('mobile_mobileform_add_follow_up_actions_forms_delete_modal_title')}
                yes={_t('mobile_drafts_button_delete')}
            />
        </>
    );
};
